/**
 * CreateCompanyPageDialog — 5-step wizard inside a PrimeReact Dialog
 * Steps: Entity Type → Identity → Loan Products → Team Access → Review
 *
 * v2.0 rebuild:
 *  - P4 dialog sizing: width 90vw / maxWidth 700px (no fixed pixel desktop)
 *  - P5 inline validation: classNames + p-invalid + <small role="alert">
 *  - P9 mandatory inputMode / autoCapitalize per field type
 *  - P16 client-side trim, regex on URL, maxLength on every text field
 *  - P21 explicit optionLabel/optionValue on every Dropdown
 *  - Hard rule 6: no hex colors anywhere — severity Tags + var(--*) tokens only
 *  - PrimeIcons replace emoji glyphs (loan types, role labels, preview)
 *  - Mobile: action buttons size="normal" in footer for 44px tap targets;
 *    product type grid col-6 sm:col-4 md:col-3; team rows flex-wrap
 */
import { useState, useRef, useEffect } from 'react';
import { Dialog }        from 'primereact/dialog';
import { Steps }         from 'primereact/steps';
import { Button }        from 'primereact/button';
import { InputText }     from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown }      from 'primereact/dropdown';
import { Checkbox }      from 'primereact/checkbox';
import { Tag }           from 'primereact/tag';
import { Avatar }        from 'primereact/avatar';
import { Divider }       from 'primereact/divider';
import { Toast }         from 'primereact/toast';
import { InputNumber }   from 'primereact/inputnumber';
import { classNames }    from 'primereact/utils';
import companyPagesBusinessService from '../../services/companyPagesBusinessService';

/* ── constants ─────────────────────────────────────────────────────── */
const ENTITY_TYPES = [
    { value: 'bank',        label: 'Private Bank',        icon: 'pi-building',         desc: 'HDFC, ICICI, Axis style',     severity: 'info' },
    { value: 'public_bank', label: 'Public Sector Bank',  icon: 'pi-building-columns', desc: 'SBI, PNB, BoB style',         severity: 'info' },
    { value: 'nbfc',        label: 'NBFC',                icon: 'pi-briefcase',        desc: 'Non-Banking Finance Co.',     severity: 'warning' },
    { value: 'fintech',     label: 'Fintech / Startup',   icon: 'pi-bolt',             desc: 'Tech-first lending',          severity: 'help' },
    { value: 'dsa',         label: 'DSA / Agency',        icon: 'pi-users',            desc: 'Direct sales agent network',  severity: 'success' },
    { value: 'broker',      label: 'Broker / Advisor',    icon: 'pi-chart-line',       desc: 'Financial advisory firm',     severity: 'secondary' },
    { value: 'legal',       label: 'Legal',               icon: 'pi-shield',           desc: 'Legal & compliance firm',     severity: 'success' },
    { value: 'technical',   label: 'Technical',           icon: 'pi-microchip',        desc: 'Tech solutions & services',   severity: 'info' },
];

const INDUSTRIES = [
    'Financial Services', 'Banking', 'Fintech', 'NBFC',
    'Insurance', 'Real Estate Finance', 'Cooperative Banking',
];

const COMPANY_SIZES = [
    'Self-employed', '1–10 employees', '11–50 employees',
    '51–200 employees', '201–1000 employees', '1000+ employees',
];

const LOAN_TYPES = [
    { value: 'home_loan',         label: 'Home Loan',         icon: 'pi pi-home' },
    { value: 'personal_loan',     label: 'Personal Loan',     icon: 'pi pi-credit-card' },
    { value: 'business_loan',     label: 'Business Loan',     icon: 'pi pi-briefcase' },
    { value: 'education_loan',    label: 'Education Loan',    icon: 'pi pi-graduation-cap' },
    { value: 'auto_loan',         label: 'Auto Loan',         icon: 'pi pi-car' },
    { value: 'gold_loan',         label: 'Gold Loan',         icon: 'pi pi-star' },
    { value: 'agriculture_loan',  label: 'Agriculture Loan',  icon: 'pi pi-sun' },
    { value: 'construction_loan', label: 'Construction Loan', icon: 'pi pi-building' },
];

const CHANNELS = ['Online', 'DSA', 'Branch', 'Mobile', 'Direct'];

const RATE_TYPES = [
    { label: 'Fixed',    value: 'fixed' },
    { label: 'Floating', value: 'floating' },
    { label: 'Hybrid',   value: 'hybrid' },
];

const ROLE_OPTIONS = [
    { label: 'Super Admin', value: 'super_admin', icon: 'pi pi-star-fill', desc: 'Full control, manage admins & products' },
    { label: 'Editor',      value: 'editor',      icon: 'pi pi-pencil',    desc: 'Create posts, manage content on this page' },
];

const STEP_ITEMS = [
    { label: 'Entity' },
    { label: 'Identity' },
    { label: 'Products' },
    { label: 'Team' },
    { label: 'Review' },
];

const URL_REGEX = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

const initials = (name) =>
    (name || '').split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?';

const labelOf = (arr, val) => arr.find((x) => x.value === val)?.label || '';
const iconOf  = (arr, val) => arr.find((x) => x.value === val)?.icon  || null;

const emptyProduct = () => ({
    _id: Date.now(), loan_type: [], product_name: '', product_code: '',
    min_amount: null, max_amount: null,
    min_roi: null, max_roi: null, rate_type: 'fixed',
    min_tenure: null, max_tenure: null,
    channels: ['Online', 'DSA'], processing_fee: null, special_features: '',
});

/* ═══════════════════════════════════════════════════════════════════ */
const CreateCompanyPageDialog = ({ visible, onHide, onCreated }) => {
    const toast    = useRef(null);
    const [step, setStep]     = useState(0);
    const [saving, setSaving] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [errors, setErrors] = useState({}); // per-field inline errors

    /* Step 1 */
    const [entityType, setEntityType] = useState('');

    /* Step 2 */
    const [identity, setIdentity] = useState({
        name: '', legalName: '', rbiLicense: '', industry: '',
        size: '', city: '', state: '', website: '', tagline: '', description: '',
    });

    /* Step 3 */
    const [products, setProducts]         = useState([]);
    const [showProdForm, setShowProdForm] = useState(false);
    const [editProd, setEditProd]         = useState(emptyProduct());
    const [productErrors, setProductErrors] = useState({});

    /* Step 4 */
    const [teamMembers, setTeamMembers]       = useState([]);
    const [allUsers, setAllUsers]             = useState([]);
    const [usersLoading, setUsersLoading]     = useState(false);
    const [selectedUser, setSelectedUser]     = useState(null);
    const [changingMemberId, setChangingMemberId] = useState(null);
    const [replaceUser, setReplaceUser]       = useState(null);

    /* ── validation per step ─────────────────────────────────────── */
    const validateStep = (s) => {
        const e = {};
        if (s === 0) {
            if (!entityType) e.entityType = 'Select an entity type to continue.';
        }
        if (s === 1) {
            const name = identity.name?.trim() || '';
            const tagline = identity.tagline?.trim() || '';
            const website = identity.website?.trim() || '';
            if (!name)                 e.name = 'Company name is required.';
            else if (name.length > 120) e.name = 'Keep the name under 120 characters.';
            if (!tagline)              e.tagline = 'Tagline is required.';
            else if (tagline.length > 120) e.tagline = 'Tagline must be 120 characters or fewer.';
            if (!identity.industry)    e.industry = 'Industry is required.';
            if (website && !URL_REGEX.test(website)) {
                e.website = 'Enter a valid URL starting with http:// or https://';
            }
        }
        if (s === 4) {
            if (!agreed) e.agreed = 'Please confirm and accept the terms before publishing.';
        }
        return e;
    };

    const next = () => {
        // Auto-save the open product form when advancing from step 2
        if (step === 2 && showProdForm) {
            const prodErr = {};
            if (!editProd.loan_type?.length) prodErr.loan_type = 'Pick at least one loan type.';
            if (!editProd.product_name?.trim()) prodErr.product_name = 'Product name is required.';
            if (Object.keys(prodErr).length) {
                setProductErrors(prodErr);
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Finish the open product',
                    detail: 'Save or cancel the product form before continuing.',
                    life: 3500,
                });
                return;
            }
            setProducts((prev) => {
                const idx = prev.findIndex((p) => p._id === editProd._id);
                return idx >= 0 ? prev.map((p) => p._id === editProd._id ? editProd : p) : [...prev, editProd];
            });
            setShowProdForm(false);
            setEditProd(emptyProduct());
            setProductErrors({});
        }

        const stepErr = validateStep(step);
        if (Object.keys(stepErr).length) {
            setErrors(stepErr);
            return;
        }
        setErrors({});
        setStep((s) => Math.min(s + 1, 4));
    };

    const prev = () => {
        setErrors({});
        setStep((s) => Math.max(s - 1, 0));
    };

    /* ── close + reset ───────────────────────────────────────────── */
    const handleClose = () => {
        setStep(0); setEntityType(''); setAgreed(false); setSaving(false);
        setErrors({}); setProductErrors({});
        setIdentity({ name: '', legalName: '', rbiLicense: '', industry: '', size: '', city: '', state: '', website: '', tagline: '', description: '' });
        setProducts([]); setTeamMembers([]); setShowProdForm(false); setEditProd(emptyProduct());
        setSelectedUser(null); setChangingMemberId(null); setReplaceUser(null);
        onHide();
    };

    /* ── publish ─────────────────────────────────────────────────── */
    const publish = async () => {
        const stepErr = validateStep(4);
        if (Object.keys(stepErr).length) {
            setErrors(stepErr);
            return;
        }
        setSaving(true);
        const payload = {
            entity_type: entityType,
            name: identity.name.trim(),
            tagline: identity.tagline.trim(),
            description: identity.description?.trim() || '',
            legal_name: identity.legalName?.trim() || '',
            rbi_license: identity.rbiLicense?.trim() || '',
            industry: identity.industry,
            company_size: identity.size,
            hq_city: identity.city?.trim() || '',
            hq_state: identity.state?.trim() || '',
            website: identity.website?.trim() || '',
            products: products.map((p) => ({ ...p, loan_type: Array.isArray(p.loan_type) ? p.loan_type.join(',') : p.loan_type })),
            team: teamMembers,
        };
        const res = await companyPagesBusinessService.createPage(payload);
        setSaving(false);
        const created = res?.success ? res.data : {
            ...payload,
            slug: identity.name.toLowerCase().replace(/\s+/g, '-'),
            follower_count: 0, my_role: 'super_admin',
            product_count: products.length, team_count: teamMembers.length + 1,
        };
        onCreated(created);
        handleClose();
    };

    /* ── product helpers ─────────────────────────────────────────── */
    const saveProduct = () => {
        const prodErr = {};
        if (!editProd.loan_type?.length) prodErr.loan_type = 'Pick at least one loan type.';
        if (!editProd.product_name?.trim()) prodErr.product_name = 'Product name is required.';
        if (Object.keys(prodErr).length) {
            setProductErrors(prodErr);
            return;
        }
        setProductErrors({});
        setProducts((prev) => {
            const idx = prev.findIndex((p) => p._id === editProd._id);
            return idx >= 0 ? prev.map((p) => p._id === editProd._id ? editProd : p) : [...prev, editProd];
        });
        setShowProdForm(false);
        setEditProd(emptyProduct());
    };

    const toggleLoanType = (loanType) => {
        setEditProd((p) => {
            const current = Array.isArray(p.loan_type) ? p.loan_type : [];
            const isSelected = current.includes(loanType);
            const updated = isSelected ? current.filter((t) => t !== loanType) : [...current, loanType];
            const co = identity.name?.trim() || 'My Company';
            const firstName = updated[0] ? labelOf(LOAN_TYPES, updated[0]) : '';
            const autoName = updated.length === 1
                ? `${co} ${firstName}`
                : (updated.length > 1 ? `${co} Loan Products` : '');
            const shouldAutoName = !p.product_name
                || LOAN_TYPES.some((lt) => p.product_name === `${co} ${lt.label}`)
                || p.product_name === `${co} Loan Products`;
            return { ...p, loan_type: updated, product_name: shouldAutoName ? autoName : p.product_name };
        });
        if (productErrors.loan_type) {
            setProductErrors((e) => ({ ...e, loan_type: undefined }));
        }
    };

    /* ── team helpers ────────────────────────────────────────────── */
    useEffect(() => {
        if (!visible) return;
        setUsersLoading(true);
        companyPagesBusinessService.getTenantUsers({ limit: 200 }).then((res) => {
            const rows = Array.isArray(res?.items) ? res.items
                       : Array.isArray(res?.data)  ? res.data
                       : Array.isArray(res)         ? res
                       : [];
            setAllUsers(rows.map((user) => {
                const nameStr = user.full_name || user.name
                    || [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
                    || user.email || '';
                return { ...user, _display: nameStr, _sub: user.email || '' };
            }));
        }).catch(() => setAllUsers([]))
          .finally(() => setUsersLoading(false));
    }, [visible]);

    const addMember = (user) => {
        if (!user) return;
        const pid = user.id;
        if (teamMembers.find((m) => (m.profile_id || m.id) === pid)) {
            setSelectedUser(null);
            return;
        }
        const nameStr = user._display || '';
        setTeamMembers((prev) => [
            ...prev,
            { ...user, profile_id: pid, name: nameStr, initials: initials(nameStr), role: 'editor' },
        ]);
        setSelectedUser(null);
    };

    const removeMember = (profileId) => {
        setTeamMembers((prev) => prev.filter((m) => (m.profile_id || m.id) !== profileId));
        if (changingMemberId === profileId) {
            setChangingMemberId(null);
            setReplaceUser(null);
        }
    };

    const applyReplace = (oldPid) => {
        if (!replaceUser) return;
        const nameStr = replaceUser._display || '';
        setTeamMembers((prev) => prev.map((m) =>
            (m.profile_id || m.id) === oldPid
                ? { ...replaceUser, profile_id: replaceUser.id, name: nameStr, initials: initials(nameStr), role: 'editor' }
                : m
        ));
        setChangingMemberId(null);
        setReplaceUser(null);
    };

    /* ── helpers for inline validation rendering ─────────────────── */
    const inputClass = (field) => classNames('w-full', { 'p-invalid': !!errors[field] });
    const fieldError = (field) => errors[field] && (
        <small role="alert" className="p-error block mt-1 text-xs">{errors[field]}</small>
    );

    /* ════════════════════════════════════════════════════════════════
     * STEP RENDERERS
     * ════════════════════════════════════════════════════════════════ */
    const renderStep0 = () => (
        <div>
            <p className="text-600 text-sm m-0 mb-3">
                This helps agents and borrowers find your page in the right category.
            </p>
            <div className="grid">
                {ENTITY_TYPES.map((et) => {
                    const selected = entityType === et.value;
                    return (
                        <div key={et.value} className="col-12 sm:col-6 lg:col-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setEntityType(et.value);
                                    if (errors.entityType) setErrors((p) => ({ ...p, entityType: undefined }));
                                }}
                                className={classNames(
                                    'w-full text-center cursor-pointer transition-all transition-duration-150',
                                    'border-round-xl p-3 surface-card',
                                    {
                                        'border-2 border-primary': selected,
                                        'border-1 surface-border': !selected,
                                    }
                                )}
                                style={{
                                    backgroundColor: selected ? 'var(--primary-50)' : undefined,
                                }}
                            >
                                <i
                                    className={classNames('pi text-2xl mb-2 block', et.icon, {
                                        'text-primary': selected,
                                        'text-500': !selected,
                                    })}
                                    aria-hidden="true"
                                />
                                <div className="text-900 font-semibold text-sm">{et.label}</div>
                                <div className="text-500 text-xs mt-1">{et.desc}</div>
                                {selected && (
                                    <i className="pi pi-check-circle text-primary text-sm mt-2 block" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
            {fieldError('entityType')}
        </div>
    );

    const renderStep1 = () => (
        <div className="grid">
            <Field label="Brand / Company name" required error={errors.name}>
                <InputText
                    value={identity.name}
                    onChange={(e) => {
                        setIdentity((p) => ({ ...p, name: e.target.value }));
                        if (errors.name) setErrors((p) => ({ ...p, name: undefined }));
                    }}
                    className={inputClass('name')}
                    placeholder="e.g. Axis Bank Home Loans"
                    maxLength={120}
                    autoCapitalize="words"
                    autoComplete="organization"
                    aria-invalid={!!errors.name}
                />
                {identity.name && !errors.name && (
                    <small className="text-500 block mt-1 text-xs">
                        Page URL: interestng.in/company/<strong>{identity.name.toLowerCase().replace(/\s+/g, '-')}</strong>
                    </small>
                )}
            </Field>

            <Field label="Legal / Registered name">
                <InputText
                    value={identity.legalName}
                    onChange={(e) => setIdentity((p) => ({ ...p, legalName: e.target.value }))}
                    className="w-full"
                    placeholder="e.g. Axis Bank Limited"
                    maxLength={200}
                    autoCapitalize="words"
                    autoComplete="organization"
                />
            </Field>

            <Field label="Industry" required error={errors.industry} half>
                <Dropdown
                    value={identity.industry}
                    options={INDUSTRIES.map((i) => ({ label: i, value: i }))}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => {
                        setIdentity((p) => ({ ...p, industry: e.value }));
                        if (errors.industry) setErrors((p) => ({ ...p, industry: undefined }));
                    }}
                    className={inputClass('industry')}
                    placeholder="Select industry"
                    showClear
                />
            </Field>

            <Field label="Company size" half>
                <Dropdown
                    value={identity.size}
                    options={COMPANY_SIZES.map((s) => ({ label: s, value: s }))}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => setIdentity((p) => ({ ...p, size: e.value }))}
                    className="w-full"
                    placeholder="Select size"
                    showClear
                />
            </Field>

            <Field label="RBI / License number" hint="Builds trust with borrowers — optional but recommended.">
                <InputText
                    value={identity.rbiLicense}
                    onChange={(e) => setIdentity((p) => ({ ...p, rbiLicense: e.target.value }))}
                    className="w-full"
                    placeholder="e.g. N-14.03216 for NBFCs"
                    maxLength={50}
                    inputMode="text"
                />
            </Field>

            <Field label="HQ city" half>
                <InputText
                    value={identity.city}
                    onChange={(e) => setIdentity((p) => ({ ...p, city: e.target.value }))}
                    className="w-full"
                    placeholder="e.g. Mumbai"
                    maxLength={80}
                    autoCapitalize="words"
                    autoComplete="address-level2"
                />
            </Field>

            <Field label="HQ state" half>
                <InputText
                    value={identity.state}
                    onChange={(e) => setIdentity((p) => ({ ...p, state: e.target.value }))}
                    className="w-full"
                    placeholder="e.g. Maharashtra"
                    maxLength={80}
                    autoCapitalize="words"
                    autoComplete="address-level1"
                />
            </Field>

            <Field label="Website URL" error={errors.website}>
                <InputText
                    value={identity.website}
                    onChange={(e) => {
                        setIdentity((p) => ({ ...p, website: e.target.value }));
                        if (errors.website) setErrors((p) => ({ ...p, website: undefined }));
                    }}
                    className={inputClass('website')}
                    placeholder="https://yourcompany.com"
                    maxLength={255}
                    inputMode="url"
                    autoCapitalize="none"
                    autoComplete="url"
                    aria-invalid={!!errors.website}
                />
            </Field>

            <Field label="Tagline" required error={errors.tagline}>
                <InputText
                    value={identity.tagline}
                    onChange={(e) => {
                        setIdentity((p) => ({ ...p, tagline: e.target.value }));
                        if (errors.tagline) setErrors((p) => ({ ...p, tagline: undefined }));
                    }}
                    className={inputClass('tagline')}
                    placeholder="e.g. India's most trusted home loan partner"
                    maxLength={120}
                    autoCapitalize="sentences"
                    aria-invalid={!!errors.tagline}
                />
                <small className="text-500 block mt-1 text-xs">{identity.tagline?.length || 0}/120 characters</small>
            </Field>

            <Field label="Description">
                <InputTextarea
                    value={identity.description}
                    onChange={(e) => setIdentity((p) => ({ ...p, description: e.target.value }))}
                    className="w-full"
                    rows={3}
                    autoResize
                    placeholder="What does your company do? What makes it unique?"
                    maxLength={5000}
                    autoCapitalize="sentences"
                />
            </Field>
        </div>
    );

    const renderStep2 = () => (
        <div>
            <p className="text-600 text-sm m-0 mb-3">
                Add loan products your company offers. These appear on your Products tab.
            </p>

            {/* Existing products list */}
            {products.map((prod) => {
                const loanTypes = Array.isArray(prod.loan_type) ? prod.loan_type : (prod.loan_type ? [prod.loan_type] : []);
                const firstIcon = iconOf(LOAN_TYPES, loanTypes[0]);
                return (
                    <div
                        key={prod._id}
                        className="flex flex-wrap align-items-center gap-3 p-3 surface-50 border-1 surface-border border-round-lg mb-2"
                    >
                        <div
                            className="flex align-items-center justify-content-center border-round flex-shrink-0"
                            style={{ width: '2.5rem', height: '2.5rem', backgroundColor: 'var(--primary-50)' }}
                        >
                            <i className={`${firstIcon || 'pi pi-box'} text-primary`} aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {prod.product_name}
                            </div>
                            <div className="text-500 text-xs">
                                {loanTypes.map((t) => labelOf(LOAN_TYPES, t)).filter(Boolean).join(' · ')}
                                {prod.min_amount && ` · ₹${prod.min_amount?.toLocaleString('en-IN')}–₹${prod.max_amount?.toLocaleString('en-IN')}`}
                                {prod.min_roi && ` · ${prod.min_roi}%–${prod.max_roi}%`}
                            </div>
                        </div>
                        <div className="flex gap-1">
                            <Button
                                icon="pi pi-pencil"
                                rounded text size="small"
                                onClick={() => { setEditProd(prod); setShowProdForm(true); setProductErrors({}); }}
                                aria-label="Edit product"
                            />
                            <Button
                                icon="pi pi-trash"
                                rounded text severity="danger" size="small"
                                onClick={() => setProducts((prev) => prev.filter((p) => p._id !== prod._id))}
                                aria-label="Delete product"
                            />
                        </div>
                    </div>
                );
            })}

            {/* Add / edit product form */}
            {showProdForm ? (
                <div className="border-1 surface-border border-round-xl p-3 surface-50 mt-2">
                    <div className="text-900 font-semibold text-sm mb-3">
                        {products.find((p) => p._id === editProd._id) ? 'Edit' : 'New'} loan product
                    </div>

                    {/* Product type chips */}
                    <div className="mb-3">
                        <label className="block text-900 text-sm font-medium mb-2">Product type *</label>
                        <div className="grid">
                            {LOAN_TYPES.map((lt) => {
                                const selected = (Array.isArray(editProd.loan_type) ? editProd.loan_type : []).includes(lt.value);
                                return (
                                    <div key={lt.value} className="col-6 sm:col-4 md:col-3">
                                        <button
                                            type="button"
                                            onClick={() => toggleLoanType(lt.value)}
                                            className={classNames(
                                                'w-full text-center cursor-pointer transition-all',
                                                'surface-card border-round-lg p-2',
                                                {
                                                    'border-2 border-primary': selected,
                                                    'border-1 surface-border': !selected,
                                                }
                                            )}
                                            style={{
                                                backgroundColor: selected ? 'var(--primary-50)' : undefined,
                                            }}
                                        >
                                            <i
                                                className={classNames(lt.icon, 'text-base mb-1 block', {
                                                    'text-primary': selected,
                                                    'text-500': !selected,
                                                })}
                                                aria-hidden="true"
                                            />
                                            <div className={classNames('text-xs font-medium', {
                                                'text-primary': selected,
                                                'text-700': !selected,
                                            })}>
                                                {lt.label}
                                            </div>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        {productErrors.loan_type && (
                            <small role="alert" className="p-error block mt-2 text-xs">{productErrors.loan_type}</small>
                        )}
                    </div>

                    <div className="grid">
                        <ProductField label="Product name" required error={productErrors.product_name} colClass="col-12 md:col-8">
                            <InputText
                                value={editProd.product_name}
                                onChange={(e) => {
                                    setEditProd((p) => ({ ...p, product_name: e.target.value }));
                                    if (productErrors.product_name) setProductErrors((er) => ({ ...er, product_name: undefined }));
                                }}
                                className={classNames('w-full', { 'p-invalid': !!productErrors.product_name })}
                                placeholder="e.g. Axis Bank Home Loan Premier"
                                maxLength={150}
                                autoCapitalize="words"
                            />
                        </ProductField>
                        <ProductField label="Rate type" colClass="col-12 md:col-4">
                            <Dropdown
                                value={editProd.rate_type}
                                options={RATE_TYPES}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setEditProd((p) => ({ ...p, rate_type: e.value }))}
                                className="w-full"
                            />
                        </ProductField>
                        <ProductField label="Min amount (₹)" colClass="col-6 md:col-3">
                            <InputNumber
                                value={editProd.min_amount}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, min_amount: e.value }))}
                                className="w-full"
                                placeholder="500000"
                                mode="decimal"
                                useGrouping
                                inputMode="numeric"
                            />
                        </ProductField>
                        <ProductField label="Max amount (₹)" colClass="col-6 md:col-3">
                            <InputNumber
                                value={editProd.max_amount}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, max_amount: e.value }))}
                                className="w-full"
                                placeholder="50000000"
                                mode="decimal"
                                useGrouping
                                inputMode="numeric"
                            />
                        </ProductField>
                        <ProductField label="Min ROI (%)" colClass="col-6 md:col-3">
                            <InputNumber
                                value={editProd.min_roi}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, min_roi: e.value }))}
                                className="w-full"
                                placeholder="7.5"
                                minFractionDigits={1}
                                maxFractionDigits={2}
                                inputMode="decimal"
                            />
                        </ProductField>
                        <ProductField label="Max ROI (%)" colClass="col-6 md:col-3">
                            <InputNumber
                                value={editProd.max_roi}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, max_roi: e.value }))}
                                className="w-full"
                                placeholder="9.5"
                                minFractionDigits={1}
                                maxFractionDigits={2}
                                inputMode="decimal"
                            />
                        </ProductField>
                        <ProductField label="Min tenure (months)" colClass="col-6">
                            <InputNumber
                                value={editProd.min_tenure}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, min_tenure: e.value }))}
                                className="w-full"
                                placeholder="60"
                                inputMode="numeric"
                            />
                        </ProductField>
                        <ProductField label="Max tenure (months)" colClass="col-6">
                            <InputNumber
                                value={editProd.max_tenure}
                                onValueChange={(e) => setEditProd((p) => ({ ...p, max_tenure: e.value }))}
                                className="w-full"
                                placeholder="360"
                                inputMode="numeric"
                            />
                        </ProductField>
                        <ProductField label="Available channels" colClass="col-12">
                            <div className="flex flex-wrap gap-3">
                                {CHANNELS.map((ch) => (
                                    <div key={ch} className="flex align-items-center gap-2">
                                        <Checkbox
                                            inputId={`ch-${ch}`}
                                            checked={editProd.channels?.includes(ch)}
                                            onChange={(e) => setEditProd((p) => ({
                                                ...p,
                                                channels: e.checked
                                                    ? [...(p.channels || []), ch]
                                                    : (p.channels || []).filter((c) => c !== ch),
                                            }))}
                                        />
                                        <label htmlFor={`ch-${ch}`} className="text-sm cursor-pointer">{ch}</label>
                                    </div>
                                ))}
                            </div>
                        </ProductField>
                        <ProductField label="Special features" colClass="col-12">
                            <InputTextarea
                                value={editProd.special_features}
                                onChange={(e) => setEditProd((p) => ({ ...p, special_features: e.target.value }))}
                                className="w-full"
                                rows={2}
                                autoResize
                                placeholder="e.g. No prepayment charges, women borrower discount…"
                                maxLength={500}
                                autoCapitalize="sentences"
                            />
                        </ProductField>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                        <Button
                            label="Save product"
                            icon="pi pi-check"
                            size="small"
                            onClick={saveProduct}
                        />
                        <Button
                            label="Cancel"
                            size="small"
                            outlined
                            severity="secondary"
                            onClick={() => { setShowProdForm(false); setEditProd(emptyProduct()); setProductErrors({}); }}
                        />
                    </div>
                </div>
            ) : (
                <Button
                    label="Add loan product"
                    icon="pi pi-plus"
                    outlined
                    size="small"
                    className="mt-2"
                    onClick={() => { setEditProd(emptyProduct()); setShowProdForm(true); setProductErrors({}); }}
                />
            )}
        </div>
    );

    const renderStep3 = () => (
        <div>
            <div
                className="flex align-items-start gap-2 p-3 border-round-lg mb-3 border-1 surface-border"
                style={{ backgroundColor: 'var(--primary-50)' }}
            >
                <i className="pi pi-info-circle text-primary mt-1 flex-shrink-0" aria-hidden="true" />
                <span className="text-700 text-sm line-height-3">
                    You're automatically the <strong>Super Admin</strong>. Grant <strong>Editor access</strong> to
                    members of your network — they can post updates and manage content on this page.
                </span>
            </div>

            <div className="text-900 font-semibold text-sm mb-2">Team members</div>

            {/* Owner row (You) */}
            <div className="flex flex-wrap align-items-center gap-3 p-3 border-round-lg mb-2 border-1 surface-border surface-50">
                <Avatar
                    label="ME"
                    shape="circle"
                    style={{
                        backgroundColor: 'var(--primary-100)',
                        color: 'var(--primary-700)',
                        fontWeight: 700,
                    }}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-900 font-semibold text-sm">You</div>
                    <div className="text-500 text-xs">Page owner — full control</div>
                </div>
                <Tag value="Super admin" icon="pi pi-star-fill" severity="warning" className="text-xs" />
            </div>

            {teamMembers.map((m) => {
                const pid = m.profile_id || m.id;
                const isChanging = changingMemberId === pid;
                const otherIds = teamMembers.filter((x) => (x.profile_id || x.id) !== pid).map((x) => x.profile_id || x.id);
                const replaceOptions = allUsers.filter((u) => !otherIds.includes(u.id) && u.id !== pid);
                return (
                    <div key={pid} className="border-1 surface-border border-round-lg mb-2 overflow-hidden">
                        <div className="flex flex-wrap align-items-center gap-3 p-3 surface-50">
                            <Avatar
                                label={m.initials || initials(m.name)}
                                shape="circle"
                                style={{
                                    backgroundColor: 'var(--primary-100)',
                                    color: 'var(--primary-700)',
                                    fontWeight: 700,
                                }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {m.name}
                                </div>
                                <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {m.email || m._sub || ''}
                                </div>
                            </div>
                            <Tag value="Editor" icon="pi pi-pencil" severity="info" className="text-xs" />
                            <div className="flex gap-1">
                                <Button
                                    icon="pi pi-refresh"
                                    rounded text size="small"
                                    tooltip="Replace user"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => { setChangingMemberId(isChanging ? null : pid); setReplaceUser(null); }}
                                    aria-label="Replace user"
                                />
                                <Button
                                    icon="pi pi-times"
                                    rounded text severity="danger" size="small"
                                    tooltip="Remove"
                                    tooltipOptions={{ position: 'top' }}
                                    onClick={() => removeMember(pid)}
                                    aria-label="Remove user"
                                />
                            </div>
                        </div>

                        {isChanging && (
                            <div className="flex flex-wrap gap-2 align-items-center p-2 border-top-1 surface-border surface-card">
                                <i className="pi pi-arrow-right-arrow-left text-primary text-xs" aria-hidden="true" />
                                <Dropdown
                                    value={replaceUser}
                                    options={replaceOptions}
                                    onChange={(e) => setReplaceUser(e.value)}
                                    optionLabel="_display"
                                    placeholder="Select replacement user…"
                                    filter
                                    filterBy="_display,_sub"
                                    className="flex-1 min-w-0"
                                    itemTemplate={(opt) => (
                                        <div className="flex align-items-center gap-2 py-1">
                                            <Avatar
                                                label={initials(opt._display)}
                                                shape="circle"
                                                style={{
                                                    width: '1.75rem',
                                                    height: '1.75rem',
                                                    fontSize: '0.7rem',
                                                    backgroundColor: 'var(--primary-100)',
                                                    color: 'var(--primary-700)',
                                                }}
                                            />
                                            <div className="min-w-0">
                                                <div className="text-900 font-semibold text-sm">{opt._display}</div>
                                                {opt._sub && <div className="text-500 text-xs">{opt._sub}</div>}
                                            </div>
                                        </div>
                                    )}
                                />
                                <Button
                                    label="Replace"
                                    icon="pi pi-check"
                                    size="small"
                                    disabled={!replaceUser}
                                    onClick={() => applyReplace(pid)}
                                />
                                <Button
                                    icon="pi pi-times"
                                    text size="small"
                                    severity="secondary"
                                    onClick={() => { setChangingMemberId(null); setReplaceUser(null); }}
                                    aria-label="Cancel replace"
                                />
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Add user as editor */}
            <div className="text-900 font-semibold text-sm mb-2 mt-3">
                <i className="pi pi-users mr-2 text-primary" aria-hidden="true" />Add user as editor
            </div>
            <div className="flex flex-wrap gap-2 align-items-stretch">
                <Dropdown
                    value={selectedUser}
                    options={allUsers.filter((u) => !teamMembers.find((m) => (m.profile_id || m.id) === u.id))}
                    onChange={(e) => setSelectedUser(e.value)}
                    optionLabel="_display"
                    placeholder={usersLoading ? 'Loading users…' : allUsers.length === 0 ? 'No users found' : 'Select a user to add…'}
                    filter
                    filterBy="_display,_sub"
                    className="flex-1 min-w-0"
                    disabled={usersLoading || allUsers.length === 0}
                    itemTemplate={(opt) => (
                        <div className="flex align-items-center gap-2 py-1">
                            <Avatar
                                label={initials(opt._display)}
                                shape="circle"
                                style={{
                                    width: '2rem',
                                    height: '2rem',
                                    fontSize: '0.7rem',
                                    backgroundColor: 'var(--primary-100)',
                                    color: 'var(--primary-700)',
                                }}
                            />
                            <div className="min-w-0">
                                <div className="text-900 font-semibold text-sm">{opt._display}</div>
                                {opt._sub && <div className="text-500 text-xs">{opt._sub}</div>}
                                {opt.primary_role && (
                                    <Tag
                                        value={opt.primary_role.replace(/_/g, ' ')}
                                        severity="secondary"
                                        className="text-xs mt-1"
                                        style={{ fontSize: '0.65rem' }}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    emptyFilterMessage="No users match your search"
                />
                <Button
                    icon="pi pi-plus"
                    label="Add"
                    size="small"
                    disabled={!selectedUser}
                    onClick={() => addMember(selectedUser)}
                />
            </div>
            {usersLoading && (
                <small className="text-500 mt-1 block text-xs">
                    <i className="pi pi-spin pi-spinner mr-1" aria-hidden="true" />Loading users…
                </small>
            )}
        </div>
    );

    const renderStep4 = () => {
        const entityMeta  = ENTITY_TYPES.find((e) => e.value === entityType);
        const entityLabel = entityMeta?.label || '';
        const entityIcon  = entityMeta?.icon  || 'pi-building';
        const entitySev   = entityMeta?.severity || 'secondary';

        return (
            <div>
                {/* Preview card — uniform white per skill (no hex gradient) */}
                <div className="border-round-xl p-4 mb-3 surface-card border-1 surface-border shadow-1">
                    <div className="flex align-items-center gap-3 mb-3">
                        <div
                            className="flex align-items-center justify-content-center border-round-lg flex-shrink-0"
                            style={{
                                width: '3.25rem',
                                height: '3.25rem',
                                backgroundColor: 'var(--primary-100)',
                                color: 'var(--primary-700)',
                                fontWeight: 800,
                                fontSize: '1.05rem',
                            }}
                        >
                            {initials(identity.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-900 font-bold text-base white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {identity.name || 'Your company'}
                            </div>
                            <div className="text-600 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                {identity.tagline || 'Tagline'}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Tag value={entityLabel} icon={`pi ${entityIcon}`} severity={entitySev} className="text-xs" />
                        <Tag value={`${products.length} ${products.length === 1 ? 'product' : 'products'}`} icon="pi pi-box" severity="secondary" className="text-xs" />
                        <Tag value={`${teamMembers.length + 1} ${teamMembers.length + 1 === 1 ? 'member' : 'members'}`} icon="pi pi-users" severity="secondary" className="text-xs" />
                    </div>
                </div>

                {/* Summary */}
                <div className="grid">
                    <div className="col-12 md:col-6 mb-3">
                        <div className="text-500 text-xs font-medium uppercase mb-1">Entity type</div>
                        <div className="text-900 font-semibold text-sm">{entityLabel}</div>
                    </div>
                    <div className="col-12 md:col-6 mb-3">
                        <div className="text-500 text-xs font-medium uppercase mb-1">Location</div>
                        <div className="text-900 text-sm">
                            {identity.city || '—'}{identity.state ? `, ${identity.state}` : ''}
                        </div>
                    </div>
                    <div className="col-12 mb-3">
                        <div className="text-500 text-xs font-medium uppercase mb-1">Loan products</div>
                        {products.length === 0 ? (
                            <span className="text-500 text-sm">No products added (you can add them later)</span>
                        ) : (
                            products.map((p) => (
                                <div key={p._id} className="text-900 text-sm mb-1">
                                    <i className="pi pi-check-circle text-green-500 mr-2" aria-hidden="true" />
                                    {p.product_name}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="col-12 mb-3">
                        <div className="text-500 text-xs font-medium uppercase mb-1">Team access</div>
                        <div className="text-900 text-sm mb-1">
                            <i className="pi pi-star-fill text-yellow-500 mr-2" aria-hidden="true" />You — Super admin
                        </div>
                        {teamMembers.map((m) => (
                            <div key={m.profile_id} className="text-900 text-sm mb-1">
                                <i className="pi pi-user text-primary mr-2" aria-hidden="true" />
                                {m.name} — {labelOf(ROLE_OPTIONS, m.role) || m.role}
                            </div>
                        ))}
                    </div>
                </div>

                <Divider />

                <div className="flex align-items-start gap-2">
                    <Checkbox
                        inputId="agree"
                        checked={agreed}
                        onChange={(e) => {
                            setAgreed(e.checked);
                            if (errors.agreed) setErrors((p) => ({ ...p, agreed: undefined }));
                        }}
                    />
                    <label htmlFor="agree" className="text-600 text-sm cursor-pointer line-height-3">
                        I confirm this page represents a real company and I have authority to create it.
                        I agree to Interestng's <span className="text-primary">Company Pages Terms</span>.
                    </label>
                </div>
                {fieldError('agreed')}
            </div>
        );
    };

    const STEP_RENDERERS = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];
    const STEP_TITLES    = ['Entity type', 'Company identity', 'Loan products', 'Team & access', 'Review & publish'];

    /* ── footer (44px tap targets — no size="small" on action buttons) ── */
    const footer = (
        <div className="flex align-items-center justify-content-between w-full gap-2 flex-wrap">
            <Button
                label="Back"
                icon="pi pi-arrow-left"
                outlined
                severity="secondary"
                disabled={step === 0}
                onClick={prev}
            />
            <div className="flex gap-2">
                <Button
                    label="Cancel"
                    text
                    severity="secondary"
                    onClick={handleClose}
                />
                {step < 4 ? (
                    <Button
                        label="Next"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        onClick={next}
                    />
                ) : (
                    <Button
                        label="Publish page"
                        icon="pi pi-check"
                        severity="success"
                        loading={saving}
                        onClick={publish}
                    />
                )}
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={handleClose}
                header={(
                    <div>
                        <div className="text-900 font-semibold text-base">Create a company page</div>
                        <div className="text-500 text-xs mt-1">Step {step + 1} of 5 — {STEP_TITLES[step]}</div>
                    </div>
                )}
                footer={footer}
                style={{ width: '90vw', maxWidth: '700px' }}
                breakpoints={{ '768px': '95vw' }}
                modal
                className="p-fluid"
                contentStyle={{ maxHeight: '65vh', overflowY: 'auto' }}
            >
                <Steps model={STEP_ITEMS} activeIndex={step} className="mb-4" readOnly />
                {STEP_RENDERERS[step]?.()}
            </Dialog>
        </>
    );
};

/* ═══════════════════════════════════════════════════════════════════
 * SUB-COMPONENTS — inline label/help/error wrappers
 * ═══════════════════════════════════════════════════════════════════ */
const Field = ({ label, required, error, hint, half, children }) => (
    <div className={half ? 'col-12 md:col-6' : 'col-12'}>
        <label className="block text-900 text-sm font-medium mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {hint && !error && (
            <small className="text-500 block mt-1 text-xs">{hint}</small>
        )}
        {error && (
            <small role="alert" className="p-error block mt-1 text-xs">{error}</small>
        )}
    </div>
);

const ProductField = ({ label, required, error, colClass = 'col-12', children }) => (
    <div className={`${colClass} mb-3`}>
        <label className="block text-900 text-sm font-medium mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {children}
        {error && (
            <small role="alert" className="p-error block mt-1 text-xs">{error}</small>
        )}
    </div>
);

export default CreateCompanyPageDialog;
